import { useState } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ActionButton } from 'core/components';
import { ActionButtonType, NotificationPosition, RequestReturnType } from 'core/enums';
import { FormProvider, useAgGridServerTableContext, useNotificationContext } from 'core/states';
import { QuoteEntityType, QuoteType, UpsertQuoteAction } from 'quotes/enums';
import { useQuoteActions } from 'quotes/hooks';
import { IBaseQuote, IReassignQuoteForm } from 'quotes/types';
import { Color, Variant } from 'theme/enums';

import { ReassignQuote } from './ReassignQuote';
import { reassignQuoteSchema } from './schema';

interface IProps {
  entityType: QuoteEntityType;
  quoteType: QuoteType;
}

export const ReassignQuoteButton = ({ entityType = QuoteEntityType.QUOTE, quoteType }: IProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const { t } = useTranslation();

  const { isLoadingTopNotificationDisplayed } = useNotificationContext();
  const { selectedRows, deselectAllRows, gridApi, updateRows } = useAgGridServerTableContext();

  const { upsertCustomerQuotes, upsertVendorQuotes } = useQuoteActions({
    entityType,
    action: UpsertQuoteAction.REASSIGN,
    notificationPosition: NotificationPosition.FORM,
  });

  const onSubmit =
    (reset?: UseFormReset<IReassignQuoteForm>) => async (data: IReassignQuoteForm) => {
      const upsertQuotesHook =
        quoteType === QuoteType.CUSTOMER ? upsertCustomerQuotes : upsertVendorQuotes;

      const { success, quotes: updatedQuotes } = await upsertQuotesHook({
        quotes: selectedRows.map((quote: IBaseQuote) => {
          return {
            id: quote.id,
            corporateUserId: data.corporateUser.id,
          };
        }),
        returnType: RequestReturnType.FULL_RECORD_AS_ARRAY,
      });

      if (!success) {
        return;
      }

      handleOnSuccess(updatedQuotes);

      reset();
    };

  const handleOnSuccess = (quotes: IBaseQuote[]) => {
    deselectAllRows();

    const ownerUserFilterData = gridApi.getFilterModel()['ownerUserName'];

    if (!ownerUserFilterData || ownerUserFilterData?.filter === quotes?.[0]?.ownerUserName) {
      // The owner user filter is not set or set to 'all'; the row must be updated
      updateRows({ update: quotes });

      return;
    }

    // The owner user filter is set to a specific user; the row must be removed
    updateRows({ remove: quotes });
  };

  const handleOnClose = (reset: UseFormReset<IReassignQuoteForm>) => {
    setIsOpen(false);

    reset();
  };

  return (
    <>
      <ActionButton
        action={{
          label: t('buttonLabelReassign', { entityType }),
          type: ActionButtonType.BUTTON,
          color: Color.NEUTRAL,
          variant: Variant.OUTLINED,
          onClick: () => setIsOpen(true),
          disabled: isLoadingTopNotificationDisplayed,
        }}
      />

      <FormProvider schema={reassignQuoteSchema()}>
        <ReassignQuote
          isOpen={isOpen}
          onSubmit={onSubmit}
          onClose={handleOnClose}
          entityType={entityType}
        />
      </FormProvider>
    </>
  );
};
